import { useEffect, useRef, useState } from 'react';
import { useGameContext } from '@/contexts/GameContext';
import { useContract } from './useContract';
import { useWallet } from './useWallet';

/**
 * Hook to manage and subscribe to game state updates
 * Automatically fetches game state when wallet connects
 * Provides methods to refresh state manually
 */
export const useGameState = () => {
  const { isConnected, address } = useWallet();
  const gameContext = useGameContext();
  const contract = useContract();

  // Track if we've already fetched for this address to prevent loops
  const [hasFetched, setHasFetched] = useState(false);
  const fetchingRef = useRef(false);
  const lastAddressRef = useRef<string | null>(null);

  // Store refs to avoid dependency issues
  const gameContextRef = useRef(gameContext);
  const contractRef = useRef(contract);

  // Update refs (no dependencies, just keep current)
  gameContextRef.current = gameContext;
  contractRef.current = contract;

  // Manual refresh function - forceRefresh bypasses the hasFetched check
  const refreshState = async (forceRefresh = false) => {
    if (fetchingRef.current) return; // Prevent concurrent fetches

    const ctx = gameContextRef.current;
    const ctr = contractRef.current;

    if (!isConnected || !address) {
      ctx.setGameState(null);
      ctx.setCombatState(null);
      return;
    }

    // If forceRefresh, clear the hasFetched flag to ensure we re-fetch
    if (forceRefresh) {
      setHasFetched(false);
    }

    fetchingRef.current = true;
    ctx.setIsLoading(true);
    ctx.setError(null);

    try {
      // Check if player has an active game
      const hasGame = await ctr.hasActiveGame();

      if (!hasGame) {
        ctx.setGameState(null);
        ctx.setCombatState(null);
        ctx.setIsLoading(false);
        fetchingRef.current = false;
        return;
      }

      // Fetch game state
      const newGameState = await ctr.getGameState();
      console.log('[useGameState] Fetched from chain - position:', newGameState?.worldX, newGameState?.worldY, 'gil:', newGameState?.gil);
      ctx.setGameState(newGameState);

      // Fetch combat state if applicable
      const newCombatState = await ctr.getCombatState();
      ctx.setCombatState(newCombatState);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch game state';
      ctx.setError(errorMessage);
      console.error('Game state refresh error:', err);
    } finally {
      ctx.setIsLoading(false);
      fetchingRef.current = false;
    }
  };

  // Auto-fetch when address changes - runs only once per address
  useEffect(() => {
    // Skip if address hasn't changed
    if (lastAddressRef.current === address && hasFetched) {
      return;
    }

    // Update tracking
    lastAddressRef.current = address;

    if (isConnected && address) {
      setHasFetched(true);
      refreshState();
    } else {
      setHasFetched(false);
      gameContextRef.current.setGameState(null);
      gameContextRef.current.setCombatState(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, isConnected]); // Only depend on address and isConnected

  return {
    gameState: gameContext.gameState,
    combatState: gameContext.combatState,
    isLoading: gameContext.isLoading,
    error: gameContext.error,
    refreshState,
    hasGame: gameContext.gameState !== null,
    isInCombat: gameContext.combatState?.active || false,
  };
};
